/**
 * Calculates great-circle distances between two points using the
 * haversine formula (keep in mind this is a "crow" distance,
 * meaning it does not count the obstacles in the way of the point
 * A and B). The haversine formula also does not account for the earth
 * not being perfectly spheroid, meaning there is a margin of error,
 * but since this is used for an approximate distance, the margin is
 * acceptable. If we want to make this even better, we could even use spherical
 * geometry from GMaps API.
 * For more info on the formula, see: https://en.wikipedia.org/wiki/Haversine_formula
 *
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @return {number}
 */
export function getDistanceFromLatLonInKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
	const earthRadiusInKilometer = 6371;
	const dLat = degreeToRad(lat2 - lat1);
	const dLon = degreeToRad(lng2 - lng1);

	// Square of half the chord length between the points
	const squareChordLength =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(degreeToRad(lat1)) * Math.cos(degreeToRad(lat2)) *
		Math.sin(dLon / 2) * Math.sin(dLon / 2);

	// Angular distance in radians.
	const angularDistance = 2 * Math.atan2(Math.sqrt(squareChordLength), Math.sqrt(1 - squareChordLength));
	return earthRadiusInKilometer * angularDistance;
}

/**
 * Takes a degree value and translates it to a radian.
 *
 * @param {number} degree
 * @return {number}
 */
function degreeToRad(degree: number): number {
	return degree * (Math.PI / 180);
}
